import { useState } from 'react';
import {
  BookOpen, Zap, TrendingUp, Users, Award, Plus, ChevronRight,
  BarChart3, Clock, CheckCircle, Star, Brain
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { RadialBarChart, RadialBar, ResponsiveContainer, Tooltip } from 'recharts';
import { toast } from 'sonner';

const EMPLOYEES = [
  { id: 1, name: 'Ravi Shankar', role: 'Backend Dev', aiScore: 78, enrolled: true, level: 'Advanced', progress: 68 },
  { id: 2, name: 'Meera Joshi', role: 'Product Manager', aiScore: 85, enrolled: true, level: 'Intermediate', progress: 45 },
  { id: 3, name: 'Kiran Patel', role: 'Data Analyst', aiScore: 62, enrolled: true, level: 'Beginner', progress: 22 },
  { id: 4, name: 'Sunita Yadav', role: 'Designer', aiScore: 55, enrolled: false, level: null, progress: 0 },
  { id: 5, name: 'Dev Sharma', role: 'DevOps', aiScore: 71, enrolled: true, level: 'Intermediate', progress: 56 },
];

const SKILL_BATCHES = [
  { id: 1, name: 'Advanced React & TypeScript', seats: 5, enrolled: 4, duration: '6 weeks', status: 'active', progress: 60 },
  { id: 2, name: 'Data Engineering with Python', seats: 5, enrolled: 3, duration: '8 weeks', status: 'active', progress: 30 },
  { id: 3, name: 'DevOps & Cloud Fundamentals', seats: 3, enrolled: 2, duration: '10 weeks', status: 'upcoming', progress: 0 },
];

const CONTENT_REQUESTS = [
  { id: 1, title: 'AI Prompt Engineering Course', status: 'in_progress', sla: '5 days left' },
  { id: 2, title: 'LangChain Workshop', status: 'completed', sla: 'Delivered' },
  { id: 3, title: 'Generative AI Basics', status: 'pending', sla: '14 days SLA' },
];

export default function TeamUpskilling() {
  const [employees, setEmployees] = useState(EMPLOYEES);

  const avgAI = Math.round(employees.filter(e => e.enrolled).reduce((s, e) => s + e.aiScore, 0) / employees.filter(e => e.enrolled).length);

  const radialData = [{ name: 'AI Readiness', value: avgAI, fill: 'hsl(var(--primary))' }];

  const handleEnroll = (id) => {
    setEmployees(prev => prev.map(e => e.id === id ? { ...e, enrolled: true, level: 'Beginner', progress: 0 } : e));
    toast.success('Employee enrolled in PromptNexus!');
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Team Upskilling</h1>
          <p className="text-muted-foreground text-sm mt-1">PromptNexus AI upskilling and SkillNexus structured training</p>
        </div>
        <Button className="gradient-primary text-primary-foreground border-0 gap-2">
          <Plus className="w-4 h-4" /> Enroll Employee
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-border kpi-glow">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                <Brain className="w-5 h-5 text-primary" />
              </div>
            </div>
            <div className="text-2xl font-bold text-foreground">{avgAI}%</div>
            <div className="text-sm text-muted-foreground">Avg AI Readiness</div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-xl bg-chart-3/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-chart-3" />
              </div>
            </div>
            <div className="text-2xl font-bold text-foreground">{employees.filter(e => e.enrolled).length}</div>
            <div className="text-sm text-muted-foreground">Enrolled on PromptNexus</div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-xl bg-chart-2/10 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-chart-2" />
              </div>
            </div>
            <div className="text-2xl font-bold text-foreground">2</div>
            <div className="text-sm text-muted-foreground">Active SkillNexus Batches</div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-xl bg-chart-4/10 flex items-center justify-center">
                <Award className="w-5 h-5 text-chart-4" />
              </div>
            </div>
            <div className="text-2xl font-bold text-foreground">3</div>
            <div className="text-sm text-muted-foreground">Content Requests</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="promptnexus">
        <TabsList className="bg-muted">
          <TabsTrigger value="promptnexus">PromptNexus</TabsTrigger>
          <TabsTrigger value="skillnexus">SkillNexus</TabsTrigger>
          <TabsTrigger value="content">Content Requests</TabsTrigger>
        </TabsList>

        {/* PromptNexus */}
        <TabsContent value="promptnexus" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-3">
              {employees.map(emp => (
                <Card key={emp.id} className="border-border">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-9 h-9 rounded-full gradient-primary flex items-center justify-center text-xs font-bold text-primary-foreground flex-shrink-0">
                        {emp.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm text-foreground">{emp.name}</span>
                          <span className="text-xs text-muted-foreground">{emp.role}</span>
                          {emp.enrolled && <span className="badge-success text-xs">Enrolled</span>}
                        </div>
                        {emp.enrolled ? (
                          <div>
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-muted-foreground">{emp.level}</span>
                              <span className="text-foreground font-medium">{emp.progress}%</span>
                            </div>
                            <Progress value={emp.progress} className="h-1.5" />
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">Not enrolled</span>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-primary">{emp.aiScore}%</div>
                        <div className="text-xs text-muted-foreground">AI Score</div>
                      </div>
                      {!emp.enrolled && (
                        <Button size="sm" className="gradient-primary text-primary-foreground border-0 text-xs" onClick={() => handleEnroll(emp.id)}>Enroll</Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="border-border">
              <CardHeader><CardTitle className="text-sm font-semibold">AI Readiness Score</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={160}>
                  <RadialBarChart cx="50%" cy="50%" innerRadius="60%" outerRadius="90%" data={radialData} startAngle={90} endAngle={-270}>
                    <RadialBar dataKey="value" cornerRadius={6} background={{ fill: 'hsl(var(--muted))' }} />
                    <Tooltip formatter={(v) => [`${v}%`, 'AI Readiness']} contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
                  </RadialBarChart>
                </ResponsiveContainer>
                <div className="text-center">
                  <div className="text-3xl font-bold text-foreground">{avgAI}%</div>
                  <div className="text-sm text-muted-foreground">Company Average</div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* SkillNexus */}
        <TabsContent value="skillnexus" className="mt-6">
          <div className="space-y-4">
            {SKILL_BATCHES.map(batch => (
              <Card key={batch.id} className="border-border">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">{batch.name}</h3>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{batch.duration}</span>
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" />{batch.enrolled}/{batch.seats} seats</span>
                      </div>
                    </div>
                    <span className={batch.status === 'active' ? 'badge-success' : 'badge-info'}>{batch.status}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="font-medium text-foreground">{batch.progress}%</span>
                  </div>
                  <Progress value={batch.progress} className="h-2" />
                  <div className="flex gap-2 mt-4">
                    <Button variant="outline" size="sm" className="border-border flex-1 text-xs">View Roster</Button>
                    <Button size="sm" className="flex-1 gradient-primary text-primary-foreground border-0 text-xs">Manage Batch</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Content Requests */}
        <TabsContent value="content" className="mt-6">
          <div className="space-y-4">
            {CONTENT_REQUESTS.map(req => (
              <Card key={req.id} className="border-border">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm text-foreground">{req.title}</p>
                        <p className="text-xs text-muted-foreground">{req.sla}</p>
                      </div>
                    </div>
                    <span className={{
                      completed: 'badge-success',
                      in_progress: 'badge-info',
                      pending: 'badge-warning',
                    }[req.status]}>{req.status.replace('_', ' ')}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
            <Button variant="outline" className="w-full border-dashed border-border gap-2">
              <Plus className="w-4 h-4" /> New Content Request
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
